import type { Session } from "@mux/sdk/v2/client"
import type { ServerConnection } from "@/context/server"
import { getRootSessionPage } from "@/transport/session"
import { sessionSnoozedUntil } from "./session-snooze"
import { sessionRevision } from "./session-revision"
import { rootSessionLoadTarget } from "./session-trim"

export type SessionPage = {
  items: Session[]
  nextCursor?: string
  hasArchived?: boolean
}

export async function fetchRootSessionPage(input: {
  server: ServerConnection.HttpBase
  directory: string
  limit: number
  cursor?: string
  signal?: AbortSignal
}) {
  return getRootSessionPage(input)
}

export async function fetchInitialRootSessionPages(input: {
  server: ServerConnection.HttpBase
  directory: string
  limit: number
  pinnedIds: readonly string[]
  signal?: AbortSignal
  fetchPage?: typeof fetchRootSessionPage
}): Promise<SessionPage> {
  const fetchPage = input.fetchPage ?? fetchRootSessionPage
  const pinned = new Set(input.pinnedIds)
  const boundarySize = rootSessionLoadTarget(input.limit)
  const items: Session[] = []
  const seenCursors = new Set<string>()
  let cursor: string | undefined
  let hasArchived: boolean | undefined

  do {
    const page = await fetchPage({
      server: input.server,
      directory: input.directory,
      limit: boundarySize,
      cursor,
      signal: input.signal,
    })
    items.push(...page.items)
    if (page.hasArchived !== undefined) hasArchived = page.hasArchived
    cursor = page.nextCursor
    if (!cursor) break
    if (seenCursors.has(cursor)) throw new Error("Session pagination returned a repeated cursor")
    seenCursors.add(cursor)
  } while (items.filter((session) => !sessionSnoozedUntil(session) && !pinned.has(session.id)).length < boundarySize)

  return { items: mergeSessionPage([], items), nextCursor: cursor, hasArchived } satisfies SessionPage
}

export async function fetchRemainingRootSessionPages(input: {
  server: ServerConnection.HttpBase
  directory: string
  limit: number
  cursor: string
  signal?: AbortSignal
  fetchPage?: typeof fetchRootSessionPage
}): Promise<SessionPage> {
  const fetchPage = input.fetchPage ?? fetchRootSessionPage
  const items: Session[] = []
  const seen = new Set<string>()
  let cursor: string | undefined = input.cursor

  while (cursor) {
    if (seen.has(cursor)) throw new Error("Session pagination returned a repeated cursor")
    seen.add(cursor)
    const page = await fetchPage({
      server: input.server,
      directory: input.directory,
      limit: input.limit,
      cursor,
      signal: input.signal,
    })
    items.push(...page.items)
    cursor = page.nextCursor
  }

  return { items: mergeSessionPage([], items), nextCursor: undefined } satisfies SessionPage
}

export function mergeSessionPage(current: Session[], incoming: Session[]) {
  const merged = new Map(current.map((session) => [session.id, session]))
  for (const session of incoming) {
    const held = merged.get(session.id)
    if (!held || sessionRevision(session) >= sessionRevision(held)) merged.set(session.id, session)
  }
  return [...merged.values()]
}
